import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { ticketsService, type TicketFilters } from '@/services/tickets'
import type { TicketInsert, TicketUpdate, MessageInsert } from '@/types/database'
import { useAuth } from '@/contexts/AuthContext'

// ============================================
// TICKETS HOOKS
// ============================================

export interface UseTicketsParams extends TicketFilters {
  page?: number
  limit?: number
}

/**
 * Hook to get all tickets with filters and pagination
 */
export function useTickets(params: UseTicketsParams = {}) {
  const { user } = useAuth()
  const { page = 1, limit = 20, ...filters } = params

  return useQuery({
    queryKey: ['tickets', params],
    queryFn: () => ticketsService.getAll(filters, page, limit),
    enabled: !!user,
  })
}

/**
 * Hook to get a single ticket by ID
 */
export function useTicket(id?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketsService.getById(id!),
    enabled: !!user && !!id,
  })
}

/**
 * Hook to get ticket statistics
 */
export function useTicketStats() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['tickets', 'stats'],
    queryFn: () => ticketsService.getStats(),
    enabled: !!user,
  })
}

/**
 * Hook to create a new ticket
 */
export function useCreateTicket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: TicketInsert) => ticketsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['tickets', 'stats'] })
    },
  })
}

/**
 * Hook to update a ticket
 */
export function useUpdateTicket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: TicketUpdate }) =>
      ticketsService.update(id, updates),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['ticket', data.id] })
      queryClient.invalidateQueries({ queryKey: ['tickets', 'stats'] })
    },
  })
}

/**
 * Hook to delete a ticket
 */
export function useDeleteTicket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => ticketsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['tickets', 'stats'] })
    },
  })
}

// ============================================
// MESSAGES HOOKS
// ============================================

/**
 * Hook to get all messages for a ticket
 */
export function useTicketMessages(ticketId?: string) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['ticket-messages', ticketId],
    queryFn: () => ticketsService.getMessages(ticketId!),
    enabled: !!user && !!ticketId,
    refetchInterval: 5000, // Refetch every 5 seconds as fallback
  })
}

/**
 * Hook to send a message
 */
export function useSendMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (message: MessageInsert) => ticketsService.sendMessage(message),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ticket-messages', data.ticket_id] })
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['ticket', data.ticket_id] })
    },
  })
}

/**
 * Hook to subscribe to real-time messages for a ticket
 */
export function useTicketMessagesRealtime(ticketId?: string) {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  useEffect(() => {
    if (!ticketId || !user) return

    const unsubscribe = ticketsService.subscribeToMessages(ticketId, (newMessage) => {
      queryClient.setQueryData(['ticket-messages', ticketId], (old: any) => {
        if (!old) return [newMessage]
        return [...old, newMessage]
      })
    })

    return unsubscribe
  }, [ticketId, user, queryClient])
}

/**
 * Hook to subscribe to real-time ticket updates
 */
export function useTicketUpdatesRealtime(ticketId?: string) {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  useEffect(() => {
    if (!ticketId || !user) return

    const unsubscribe = ticketsService.subscribeToTicketUpdates(ticketId, (updatedTicket) => {
      queryClient.setQueryData(['ticket', ticketId], updatedTicket)
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
    })

    return unsubscribe
  }, [ticketId, user, queryClient])
}

// ============================================
// ASSIGNMENT HOOKS
// ============================================

/**
 * Hook to assign a ticket to current admin
 */
export function useAssignTicketToMe() {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  return useMutation({
    mutationFn: (ticketId: string) => {
      if (!user) throw new Error('User not authenticated')
      return ticketsService.assignToMe(ticketId, user.id)
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['ticket', data.id] })
    },
  })
}

/**
 * Hook to unassign a ticket
 */
export function useUnassignTicket() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (ticketId: string) => ticketsService.unassign(ticketId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      queryClient.invalidateQueries({ queryKey: ['ticket', data.id] })
    },
  })
}
